import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../../shared/lib/common/Alert';
import { CustomPhoneController } from '../common/CustomPhoneController';
import { BUTTON_TEXT_CANCEL, BUTTON_TEXT_VERIFY, INVALID_AUTH_TOKEN, LABEL_PHONE_NUMBER, LABEL_YOUR_MOBILE_PHONE_NUMBER, PHONE_NO_CONSENT_TEXT } from '../../../../shared/lib/constants';
import { withTrademark } from '../../../../shared/lib/common/Tokenizer';
import palette from '../../../../shared/lib/theme/palette';
import { backDropStyle } from '../../../../shared/lib/theme/styleConstants';
import { CALLER_ID_ALREADY_ADDED, MSG_PHONE_NUMBER_UPDATED, MSG_PLEASE_CHECK_YOUR_PHONE, PHONE_ALREADY_VERIFIED } from '../../app/constants';
import { useAppContext } from '../../app/contexts/AppContext';
import { EditContactNumberProps, PhoneNumberInput } from '../../app/interfaceTypes';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { editPhoneNumberValidationSchema } from '../../app/validationSchema';
import { OtpVerification, UserStatus, useFetchPhoneVerifiedLazyQuery, useSendOtpV2Mutation } from '../../generated';
import { VerifyOtp } from '../VerifyOtp';
import { CustomModal } from '../common/CustomModal';

export const EditContactNumber: FC<EditContactNumberProps> = ({ setHideBackDrop, isReverifyMode = false }) => {
  const { currentUser, setCurrentUser, isPhoneEdit, dispatch } = useAppContext();
  const { phoneNumber } = currentUser ?? {};
  const { borderLight } = palette || {};
  const [editContactSteps, setEditContactSteps] = useState<boolean>(false);
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState<string>('');
  const [infoSteps, setInfoSteps] = useState<boolean>(false);
  const [code, setCode] = useState<string | null | undefined>(undefined);
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);
  const [consentChecked, setConsentChecked] = useState<boolean>(false);

  const emailEditClose = () => {
    dispatch({ type: ActionType.SET_IS_PHONE_EDIT, isPhoneEdit: false });
    setHideBackDrop(false);
    setEditContactSteps(false);
    setConsentChecked(false);
    dispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: 60 });
    stopPolling();
  };

  const [fetchPhoneVerified, { data, startPolling, stopPolling }] = useFetchPhoneVerifiedLazyQuery({
    fetchPolicy: 'network-only',
    onError({ message }) {
      Alert.error(message);
    },
    pollInterval: 5000,

    onCompleted(data) {
      const { me } = data;
      const { phoneNumber, phoneNumberSid } = me;

      if ((`+${displayPhoneNumber}` === phoneNumber || `${displayPhoneNumber}` === phoneNumber) && phoneNumberSid) {
        stopPolling();
        setIsPhoneVerified(true);
      }
    }
  });

  const [sendOtpV2, { loading: sendOtpV2Loading }] = useSendOtpV2Mutation({
    onError({ message }) {
      if (message === INVALID_AUTH_TOKEN) Alert.error(INVALID_AUTH_TOKEN);
      else if (message === CALLER_ID_ALREADY_ADDED) {
        Alert.success(PHONE_ALREADY_VERIFIED);
        fetchPhoneVerified();
        emailEditClose();
      } else Alert.error(message);
    },

    onCompleted(data) {
      !isPhoneVerified && (async () => await fetchPhoneVerified())();

      const { sendOtpV2 } = data ?? {};
      const { code } = sendOtpV2 ?? {};

      if (sendOtpV2) {
        setCode(code);
        startPolling(5000);
        dispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: import.meta.env.VITE_SEND_OTP_V2_TIME_LEFT ?? 300 });
        setEditContactSteps(true);
        Alert.success(MSG_PLEASE_CHECK_YOUR_PHONE);
        setInfoSteps(true);
      }
    }
  });

  const methods = useForm<PhoneNumberInput, any, PhoneNumberInput>({
    mode: 'all',
    resolver: yupResolver(editPhoneNumberValidationSchema),

    defaultValues: {
      phoneNumber: phoneNumber?.startsWith('+') ? phoneNumber.slice(1) : (phoneNumber ?? '')
    }
  });

  const { handleSubmit, setValue } = methods;

  useEffect(() => {
    phoneNumber && setValue('phoneNumber', phoneNumber.startsWith('+') ? phoneNumber.slice(1) : phoneNumber);
  }, [phoneNumber]);

  const onSubmit: SubmitHandler<PhoneNumberInput> = async (data) => {
    const { phoneNumber: newPhoneNumber } = data;
    setDisplayPhoneNumber(newPhoneNumber);

    await sendOtpV2({
      variables: {
        sendOtpInputV2: {
          phoneNumber: newPhoneNumber?.startsWith('+') ? newPhoneNumber : `+${newPhoneNumber}`,
          otpType: OtpVerification.PhoneVerify
        }
      }
    });
  };

  useEffect(() => {
    if (data?.me.phoneNumberSid) {
      setCurrentUser({ ...currentUser, phoneNumberSid: data.me.phoneNumberSid } as typeof currentUser);
    }

    if (!isPhoneEdit) return;

    if ((data?.me.phoneNumber === `+${displayPhoneNumber}` && data?.me.phoneNumberSid) || isPhoneVerified) {
      const { createdAt, email, emailVerified, id, status, updatedAt, enablePhoneRinger, defaultMessage, showLogo } = currentUser ?? {};
      const phoneNumber = displayPhoneNumber;
      const updatedUser = {
        ...currentUser,
        updatedAt,
        status: status || UserStatus.Active,
        phoneNumber: phoneNumber?.startsWith('+') ? phoneNumber : `+${phoneNumber}`,
        phoneNumberSid: data?.me.phoneNumberSid ?? currentUser?.phoneNumberSid,
        id: id || '',
        createdAt: createdAt || '',
        email: email || '',
        emailVerified: !!emailVerified,
        isPhoneVerified: true,
        defaultMessage: defaultMessage ?? true,
        enablePhoneRinger: enablePhoneRinger ?? false,
        showLogo: showLogo ?? true
      };

      if (phoneNumber !== '') setCurrentUser(updatedUser);

      emailEditClose();
      Alert.success(MSG_PHONE_NUMBER_UPDATED);
    }
  }, [data?.me.phoneNumber, data?.me.phoneNumberSid, isPhoneVerified, isPhoneEdit]);

  return (
    <Box>
      <CustomModal maxWidth="600px" isOpen={isPhoneEdit} hideBackdrop>
        {editContactSteps ? (
          <Box padding="24px 0">
            <VerifyOtp isTpin={false} displayPhoneNumber={displayPhoneNumber} emailEditClose={emailEditClose} sendOtp={sendOtpV2} infoSteps={infoSteps} resetPhone code={code} />
          </Box>
        ) : (
          <Box>
            <Box position="relative">
              <Typography variant="h6" sx={{ fontWeight: '700', color: 'blackOne', padding: '24px 0' }}>
                {LABEL_YOUR_MOBILE_PHONE_NUMBER}
              </Typography>
            </Box>

            <FormProvider {...methods}>
              <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <CustomPhoneController controllerLabel={LABEL_PHONE_NUMBER} controllerName="phoneNumber" isDisabled={isReverifyMode} />

                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />}
                  label={<Typography variant="subtitle2">{withTrademark(PHONE_NO_CONSENT_TEXT)}</Typography>}
                />

                <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
                  <Button fullWidth onClick={emailEditClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                    {BUTTON_TEXT_CANCEL}
                  </Button>
                  <Button fullWidth type="submit" variant="contained" color="primary" disabled={sendOtpV2Loading || !consentChecked} endIcon={sendOtpV2Loading && <CircularProgress size={20} />}>
                    {BUTTON_TEXT_VERIFY}
                  </Button>
                </Box>
              </form>
            </FormProvider>
          </Box>
        )}
      </CustomModal>

      {isPhoneEdit && <Box sx={backDropStyle} />}
    </Box>
  );
};
